import React, { ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { ManagedPaymentMethod } from '../util/Managed'
import {
  assetLine, assetSubtitle, methodAssetLogo, methodIsOffHomeNetwork, methodNetworkLabel, methodNetworkLogo, tickerLabel
} from '../util/AssetLabels'

// ---------------------------------------------------------------------------------------------------------------------
// The one way an asset looks in a list, a dropdown or a chip: its own logo, a network badge when it is away from home,
// the ticker, and the name with the network under it.
// ---------------------------------------------------------------------------------------------------------------------

const IconShell = styled.span<{ $size: number }>`
  display: inline-block;
  flex: 0 0 auto;
  height: ${({ $size }) => $size}px;
  position: relative;
  width: ${({ $size }) => $size}px;
`

const Logo = styled.span<{ $logo?: string, $size: number }>`
  background-color: ${({ $logo }) => $logo ? 'transparent' : 'var(--surface-pressed, #eef2f7)'};
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 999px;
  display: block;
  height: ${({ $size }) => $size}px;
  width: ${({ $size }) => $size}px;
`

const Badge = styled.span<{ $size: number }>`
  align-items: center;
  background: var(--surface, #ffffff);
  border-radius: 999px;
  bottom: -${({ $size }) => Math.round($size * 0.08)}px;
  box-shadow: 0 0 0 1.5px var(--surface, #ffffff);
  display: flex;
  height: ${({ $size }) => Math.round($size * 0.48)}px;
  justify-content: center;
  position: absolute;
  right: -${({ $size }) => Math.round($size * 0.12)}px;
  width: ${({ $size }) => Math.round($size * 0.48)}px;
`

const BadgeLogo = styled.span<{ $logo?: string }>`
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 999px;
  display: block;
  height: 100%;
  width: 100%;
`

export interface AssetIconProps {
  method: ManagedPaymentMethod
  size?: number
  /** Show the network badge when the asset is away from its home chain. On by default. */
  badge?: boolean
  style?: React.CSSProperties
}

/** The asset's logo, with the network as a small badge when that tells the reader something. */
export const AssetIcon = ({ method, size = 28, badge = true, style }: AssetIconProps): ReactElement => {
  const showBadge = badge && methodIsOffHomeNetwork(method)
  return <IconShell $size={size} style={style} aria-hidden='true'>
    <Logo $logo={methodAssetLogo(method)} $size={size} />
    {showBadge && <Badge $size={size} title={methodNetworkLabel(method)}>
      <BadgeLogo $logo={methodNetworkLogo(method)} />
    </Badge>}
  </IconShell>
}

/** The logo of a network on its own, for pickers that choose a chain rather than an asset. */
export const NetworkIcon = ({ method, size = 28, style }: Omit<AssetIconProps, 'badge'>): ReactElement =>
  <IconShell $size={size} style={style} aria-hidden='true'>
    <Logo $logo={methodNetworkLogo(method)} $size={size} />
  </IconShell>

const Row = styled.span`
  align-items: center;
  display: flex;
  gap: 10px;
  min-width: 0;
  text-align: left;
`

const RowText = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const RowTitle = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowSubtitle = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12px;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RowEnd = styled.span`
  color: var(--text-muted, #6b7280);
  flex: 0 0 auto;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
`

export interface AssetRowProps {
  method: ManagedPaymentMethod
  size?: number
  /** Name the network. Off for balances that are not tied to one network. */
  network?: boolean
  /** Shown on the right, such as an available balance. */
  trailing?: ReactNode
  /** Replaces the second line when the caller has something better to say there. */
  subtitle?: ReactNode
  style?: React.CSSProperties
}

/** A list or dropdown row: icon, ticker, and the name with the network under it. */
export const AssetRow = (
  { method, size = 28, network = true, trailing, subtitle, style }: AssetRowProps
): ReactElement =>
  <Row style={style}>
    <AssetIcon method={method} size={size} badge={network} />
    <RowText>
      <RowTitle>{tickerLabel(method.ticker)}</RowTitle>
      <RowSubtitle>{subtitle ?? assetSubtitle(method, { network })}</RowSubtitle>
    </RowText>
    {trailing !== undefined && <RowEnd>{trailing}</RowEnd>}
  </Row>

/** A network as a row: its logo and name, for pickers that choose where something settles. */
export const NetworkRow = ({ method, size = 28, trailing, subtitle, style }: AssetRowProps): ReactElement =>
  <Row style={style}>
    <NetworkIcon method={method} size={size} />
    <RowText>
      <RowTitle>{methodNetworkLabel(method)}</RowTitle>
      {subtitle !== undefined && <RowSubtitle>{subtitle}</RowSubtitle>}
    </RowText>
    {trailing !== undefined && <RowEnd>{trailing}</RowEnd>}
  </Row>

const Inline = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 7px;
  min-width: 0;
  white-space: nowrap;
`

const InlineNetwork = styled.span`
  color: var(--text-soft, #7d8492);
`

export interface AssetInlineProps {
  method: ManagedPaymentMethod
  size?: number
  network?: boolean
  style?: React.CSSProperties
}

/** One line with the icon: "USDC on Polygon", the network in a quieter tone. */
export const AssetInline = ({ method, size = 18, network = true, style }: AssetInlineProps): ReactElement => {
  const networkLabel = network ? methodNetworkLabel(method) : ''
  return <Inline style={style}>
    <AssetIcon method={method} size={size} badge={network} />
    <span>
      {tickerLabel(method.ticker)}
      {networkLabel && <InlineNetwork> on {networkLabel}</InlineNetwork>}
    </span>
  </Inline>
}

export { assetLine }
